// src/hooks/useTpnCart.ts
import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { shouldShowCartIcon } from '../utils/tpnUtils';
import { checkIfRestaurantClosed, formatStartTimesToEST, isInBreakHours } from '../utils/reservationUtils';
import { PaymentPreferences } from '../types/restaurant';

/**
 * Custom hook for determining cart icon visibility based on TPN configuration
 * @param showCartIcon - Whether cart icon is enabled in widget settings
 * @returns boolean indicating if cart icon should be shown
 */
export const useTpnCart = (showCartIcon: boolean): boolean => {
  // Get TPN configuration from Redux store
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  // Get selected restaurant from Redux store
  const selectedRestaurant = useAppSelector((state) => state?.restaurant?.selectedRestaurant);
  
  // Get table availability state from Redux store
  const { 
    loading: tableLoading, 
    time_slots 
  } = useAppSelector((state) => state.tableAvailability);
  
  // Get site content for restaurant hours
  const siteContent = useAppSelector((state) => state.siteContent);
  
  // Memoize the cart visibility logic with complete reservation checks
  const shouldShowCart = useMemo(() => {
    // Check 0: isAddToCartEnabled (HIGHEST PRIORITY - from backend)
    // Parse payment_perferences if it's a JSON string
    let paymentPrefs: PaymentPreferences | null = null;
    if (selectedRestaurant?.payment_perferences) {
      try {
        paymentPrefs = typeof selectedRestaurant.payment_perferences === 'string'
          ? JSON.parse(selectedRestaurant.payment_perferences)
          : selectedRestaurant.payment_perferences;
      } catch (error) {
        console.error('Failed to parse payment_perferences:', error);
      }
    }
    
    // Default to true if key doesn't exist (backward compatible)
    const isAddToCartEnabled = paymentPrefs?.isAddToCartEnabled ?? true;
    if (!isAddToCartEnabled) return false;
    
    // Check 1: Restaurant must be open today
    const todayDate = new Date().toISOString().split('T')[0];
    const isRestaurantClosed = checkIfRestaurantClosed(todayDate, siteContent);
    if (isRestaurantClosed) return false;
    
    // Check 1.5: Hide cart during break hours (between lunch and dinner)
    if (isInBreakHours(siteContent)) return false;
    
    // Check 2: TPN Configuration must exist
    const hasTpnConfig = shouldShowCartIcon(showCartIcon, tpnConfig);
    if (!hasTpnConfig) return false;
    
    // Check 3: Don't show cart while loading table availability
    // if (tableLoading) return false;
    
    // Check 4: Time slots must exist and have future availability
    // Apply EST-based filtering (same logic as TableReservationsPage lines 58-63)
    if (!time_slots || time_slots.length === 0) return false;
    const filteredSlots = formatStartTimesToEST(time_slots, todayDate);
    if (filteredSlots.length === 0) return false;
    
    // All checks passed - show cart
    return true;
  }, [selectedRestaurant, siteContent, showCartIcon, tpnConfig, tableLoading, time_slots]);
  
  return shouldShowCart;
};

/**
 * Custom hook for getting TPN configuration status
 * @returns object with TPN status information
 */
export const useTpnStatus = () => {
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  return useMemo(() => ({
    isActive: !!(tpnConfig?.tpn_config && tpnConfig.tpn_config.length > 0),
    configCount: tpnConfig?.tpn_config?.length || 0,
    hasConfig: !!tpnConfig,
    config: tpnConfig
  }), [tpnConfig]);
};
